// Canonical reorderable-column key list for the public results table.
// Kept in sync with `_COLUMN_ORDER_KEYS` in apps/api/app/schemas/branding.py
// and `ColumnKey` in apps/search/src/components/results-table/column-defs.ts.
export type ColumnKey =
  | 'quality'
  | 'imprint'
  | 'payment'
  | 'license'
  | 'publishingModel';

export const COLUMN_KEYS: ColumnKey[] = [
  'quality',
  'imprint',
  'payment',
  'license',
  'publishingModel',
];

// User-facing labels shown in the reorder control. "imprint" and "payment"
// correspond to the "Imprint" and "Payment title" text fields under the
// Text tab, which accounts can rename per-account (e.g. "Payment title" ->
// "Agreement") — this list intentionally shows the original/generic field
// names, not any particular account's customized text.
export const COLUMN_LABELS: Record<ColumnKey, string> = {
  quality: 'Quality',
  imprint: 'Imprint',
  payment: 'Payment title',
  license: 'Licence Options',
  publishingModel: 'Publishing Model',
};

/**
 * Reconciles a stored `column_order` against the columns actually applicable
 * for this account (i.e. drops "quality" when the account has no quality_lists
 * extension), and appends any applicable column missing from the stored value.
 * Falls back cleanly to the default order when the stored value is absent,
 * invalid, incomplete, or contains unknown keys.
 */
export function reconcileColumnOrder(
  stored: string[] | undefined,
  applicableKeys: ColumnKey[],
): ColumnKey[] {
  const applicableSet = new Set(applicableKeys);
  const fromStored = Array.isArray(stored)
    ? stored.filter((key): key is ColumnKey =>
        applicableSet.has(key as ColumnKey),
      )
    : [];
  const seen = new Set(fromStored);
  const missing = applicableKeys.filter((key) => !seen.has(key));
  return [...fromStored, ...missing];
}
